"use client"

/**
 * FAQ on the v3 rungs. Not in the Figma frame — Eugene's call was "take our
 * existing section, then rework it", and the rework is typography: the
 * shared `FaqSection` sets its heading in display-sm regular and its
 * questions in title-md, which is the old ladder. This page's sections all
 * run display-md emphasized for the heading, an emphasized body rung for
 * titles, body-lg for copy and the DS outline pill for a secondary link.
 *
 * The homepage passes CMS FAQs (`items`, answers as HTML, AHD-14224); /pricing
 * passes a few of the site's `home.faq*` keys. One question open at a time,
 * the same AnimatePresence reveal as the shared section, which stays
 * untouched: it renders on every /go page.
 */

import { useState } from "react"
import Link from "next/link"
import { m, AnimatePresence } from "framer-motion"
import { useTranslations } from "@/lib/intl/use-translations"
import { homeKeys as k } from "@/i18n/home-keys"
import { useLocalePath } from "@/lib/use-localized-navigation"
import type { SafeHtml } from "@/lib/safe-html"
import { buttonVariants } from "@/components/ui/button"
import { ChevronDownIcon } from "@/components/ui/icons"
import { HOMEPAGE_FAQ_KEYS } from "@/app/(frontend)/[country]/[locale]/_components/faq-section"
import { cn } from "@/lib/utils"

type FaqKey = (typeof HOMEPAGE_FAQ_KEYS)[number]

/** A CMS FAQ as the homepage passes it: the answer is the CMS's HTML,
 *  sanitized and locale-prefixed on the server (`home-v4-page-data.ts`). */
export type HomeV4FaqItem = { question: string; answer: SafeHtml }

/**
 * `items`: the homepage's CMS FAQs, in order. `keys`: the site's own
 * questions for another page; /pricing keeps only the ones about paying
 * (Eugene, 30.09).
 */
export function HomeV4Faq(
  props: { section: string } & (
    | { items: readonly HomeV4FaqItem[] }
    | { keys: readonly FaqKey[] }
  )
) {
  const { section } = props
  const t = useTranslations("home")
  const lp = useLocalePath()
  const [open, setOpen] = useState<number | null>(null)
  const entries =
    "items" in props
      ? props.items.map((f) => ({ q: f.question, a: f.answer, html: true }))
      : props.keys.map((entry) => ({
          q: t(entry.q),
          a: t(entry.a),
          html: false,
        }))

  return (
    <section className="py-10 md:pt-[92px] md:pb-[100px]">
      <div
        className={`${section} grid grid-cols-1 gap-10 md:grid-cols-[1fr_2fr] md:gap-12`}
      >
        <div>
          <h2 className="type-display-md-emphasized max-w-[386px] text-neutral-900">
            {t(k.faqHeading)}
          </h2>
          <Link
            href={lp("/faq")}
            className={cn(buttonVariants({ variant: "outline" }), "mt-8 px-8")}
          >
            {t(k.faqLink)}
          </Link>
        </div>

        <div className="divide-y divide-neutral-200 border-t border-neutral-200">
          {entries.map((entry, i) => {
            const isOpen = open === i
            return (
              <div key={entry.q}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="flex w-full cursor-pointer items-center justify-between gap-6 py-5 text-left"
                >
                  <span className="type-body-xl-emphasized text-neutral-900">
                    {entry.q}
                  </span>
                  <ChevronDownIcon
                    className={cn(
                      "shrink-0 text-neutral-400 transition-transform duration-200",
                      isOpen && "rotate-180"
                    )}
                  />
                </button>
                {/* safe-animate-presence: conditional-mount — gated by `isOpen=true` */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <m.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                      role="region"
                    >
                      {entry.html ? (
                        <div
                          className="type-body-lg [&_a]:text-aubergine-700 max-w-[640px] pb-6 text-neutral-600 [&_a]:underline [&_a]:underline-offset-2 [&_li]:mb-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
                          dangerouslySetInnerHTML={{ __html: entry.a }}
                        />
                      ) : (
                        <p className="type-body-lg max-w-[640px] pb-6 text-neutral-600">
                          {entry.a}
                        </p>
                      )}
                    </m.div>
                  )}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
